import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Title,
  Paper,
  Select,
  NumberInput,
  TextInput,
  Button,
  Stack,
  Container,
  Group,
  Modal,
  ScrollArea,
  Card,
  Text,
  Textarea,
  Badge,
  ActionIcon,
  Tooltip,
  Tabs,
  Divider,
  SimpleGrid,
  Grid,
  Progress,
  Box,
  Center,
  Loader,
  Alert,
  SegmentedControl
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconPlus,
  IconTrash,
  IconEdit,
  IconX,
  IconHistory,
  IconSearch,
  IconGiftCard,
  IconCircleCheck,
  IconAlertCircle
} from '@tabler/icons-react';
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  doc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  runTransaction,
  orderBy,
  startAt,
  endAt,
  limit,
  where,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { obtenerCamposBusquedaGiftcard, normalizarValorBusqueda } from '../utils/giftcardSearch';
import { getModoUso, permiteUsoPorMonto } from '../utils/giftcardUsage';
import GiftcardSalesReport from '../components/GiftcardSalesReport';

const RESULT_LIMIT = 30;
const ACTIVITY_LIMIT = 15;

const formatearMonto = (monto = 0) => Number(monto || 0).toLocaleString('es-CL');

const normalizarEmail = (email = '') => email.trim().toLowerCase();

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizarTextoFormulario = (value = '') => String(value ?? '').trim().replace(/\s+/g, ' ');

const formatearFechaActividad = (value) => {
  const fecha = value?.toDate?.() || (value ? new Date(value) : null);
  if (!fecha || Number.isNaN(fecha.getTime())) return 'Fecha no disponible';

  return fecha.toLocaleString('es-CL', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTipoLabel = (tipo) => {
  if (tipo === 'giftcard') return 'Giftcard';
  if (tipo === 'prepagado') return 'Servicio Prepagado';
  if (tipo === 'exento') return 'Exento';
  return 'Sin tipo';
};

const esModoMonto = (giftcard) => getModoUso(giftcard) === 'monto';

const getSantiagoDateKey = (date = new Date()) => (
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date)
);

const normalizarFechaVencimiento = (value) => {
  if (!value) return null;

  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : getSantiagoDateKey(date);
  }

  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const parsedDate = new Date(raw);
  return Number.isNaN(parsedDate.getTime()) ? null : getSantiagoDateKey(parsedDate);
};

const formatearFechaVencimiento = (value) => {
  const fechaKey = normalizarFechaVencimiento(value);
  if (!fechaKey) return '';

  const [year, month, day] = fechaKey.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12);

  return date.toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const estaGiftcardVencida = (giftcard) => {
  const fechaVencimiento = normalizarFechaVencimiento(giftcard?.fechaVencimiento);
  return Boolean(fechaVencimiento && getSantiagoDateKey() > fechaVencimiento);
};

const getEstadoVigencia = ({ fechaVencimientoLabel, fallbackLabel, fallbackColor, descripcionUso }) => {
  if (!fechaVencimientoLabel) {
    return {
      label: fallbackLabel,
      color: fallbackColor,
      descripcion: descripcionUso
    };
  }

  return {
    label: 'Vigente',
    color: 'green',
    descripcion: `Vigente hasta ${fechaVencimientoLabel} · ${descripcionUso}`
  };
};

const normalizarTerminoBusqueda = (value) => value.trim().replace(/\s+/g, ' ');

const getNumeroBusqueda = (value) => {
  if (!/^\d+$/.test(value)) return null;

  const numero = Number(value);
  return Number.isSafeInteger(numero) ? numero : null;
};

const crearVariantesBusqueda = (value) => {
  const termino = normalizarTerminoBusqueda(value);
  if (!termino) return [];

  const capitalizado = termino
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letra) => letra.toUpperCase());

  return [...new Set([termino, termino.toLowerCase(), termino.toUpperCase(), capitalizado])];
};

const mapDocGiftcard = (snapshot) => ({ id: snapshot.id, ...snapshot.data() });

const mezclarResultadosGiftcards = (resultados) => {
  const map = new Map();

  resultados.flat().forEach((giftcard) => {
    if (giftcard?.id && !map.has(giftcard.id)) {
      map.set(giftcard.id, giftcard);
    }
  });

  return [...map.values()];
};

const normalizarFechaUso = (fecha) => {
  if (!fecha) return null;
  if (typeof fecha.toDate === 'function') return fecha.toDate();

  const parsedDate = new Date(fecha);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatearFechaUso = (fecha) => {
  const fechaNormalizada = normalizarFechaUso(fecha);

  if (!fechaNormalizada) return 'Fecha no registrada';

  return fechaNormalizada.toLocaleString('es-CL', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};

const obtenerUltimoUso = (giftcard) => {
  const usos = giftcard.historialUsos || [];

  return usos
    .map((uso) => normalizarFechaUso(uso.fecha))
    .filter(Boolean)
    .sort((a, b) => b.getTime() - a.getTime())[0] || null;
};

const obtenerEstadoUso = (giftcard) => {
  if (esModoMonto(giftcard)) {
    const montoInicial = Number(giftcard.montoInicial || 0);
    const saldoDisponible = Math.max(Number(giftcard.saldoDisponible || 0), 0);
    const montoUsado = Math.max(Number(giftcard.montoUsado || 0), 0);
    const progreso = montoInicial > 0 ? Math.min((montoUsado / montoInicial) * 100, 100) : 0;
    const fechaVencimientoLabel = formatearFechaVencimiento(giftcard.fechaVencimiento);

    if (estaGiftcardVencida(giftcard)) {
      return {
        label: 'Vencida',
        color: 'gray',
        descripcion: fechaVencimientoLabel ? `Venció el ${fechaVencimientoLabel}` : 'Giftcard vencida',
        usados: montoUsado,
        disponibles: 0,
        disponiblesOriginales: saldoDisponible,
        cantidad: montoInicial,
        progreso: 100,
        modoUso: 'monto',
        vencida: true,
        fechaVencimientoLabel
      };
    }

    if (montoUsado <= 0) {
      const estadoVigencia = getEstadoVigencia({
        fechaVencimientoLabel,
        fallbackLabel: 'Sin usar',
        fallbackColor: 'blue',
        descripcionUso: `$ ${formatearMonto(saldoDisponible)} disponibles`
      });

      return {
        label: estadoVigencia.label,
        color: estadoVigencia.color,
        descripcion: estadoVigencia.descripcion,
        usados: montoUsado,
        disponibles: saldoDisponible,
        cantidad: montoInicial,
        progreso,
        modoUso: 'monto',
        vencida: false,
        fechaVencimientoLabel
      };
    }

    if (saldoDisponible <= 0) {
      return {
        label: 'Usada / agotada',
        color: 'red',
        descripcion: `$ ${formatearMonto(montoUsado)} usados`,
        usados: montoUsado,
        disponibles: saldoDisponible,
        cantidad: montoInicial,
        progreso,
        modoUso: 'monto',
        vencida: false,
        fechaVencimientoLabel
      };
    }

    const estadoVigencia = getEstadoVigencia({
      fechaVencimientoLabel,
      fallbackLabel: 'Uso parcial',
      fallbackColor: 'yellow',
      descripcionUso: `$ ${formatearMonto(saldoDisponible)} disponibles`
    });

    return {
      label: estadoVigencia.label,
      color: estadoVigencia.color,
      descripcion: estadoVigencia.descripcion,
      usados: montoUsado,
      disponibles: saldoDisponible,
      cantidad: montoInicial,
      progreso,
      modoUso: 'monto',
      vencida: false,
      fechaVencimientoLabel
    };
  }

  const cantidad = Number(giftcard.cantidad || 0);
  const usados = Number(giftcard.serviciosUsados || 0);
  const disponibles = Math.max(cantidad - usados, 0);
  const progreso = cantidad > 0 ? Math.min((usados / cantidad) * 100, 100) : 0;
  const fechaVencimientoLabel = formatearFechaVencimiento(giftcard.fechaVencimiento);

  if (estaGiftcardVencida(giftcard)) {
    return {
      label: 'Vencida',
      color: 'gray',
      descripcion: fechaVencimientoLabel ? `Venció el ${fechaVencimientoLabel}` : 'Giftcard vencida',
      usados,
      disponibles: 0,
      disponiblesOriginales: disponibles,
      cantidad,
      progreso: 100,
      modoUso: 'servicio',
      vencida: true,
      fechaVencimientoLabel
    };
  }

  if (usados <= 0) {
    const estadoVigencia = getEstadoVigencia({
      fechaVencimientoLabel,
      fallbackLabel: 'Sin usar',
      fallbackColor: 'blue',
      descripcionUso: `${disponibles} disponibles`
    });

    return {
      label: estadoVigencia.label,
      color: estadoVigencia.color,
      descripcion: estadoVigencia.descripcion,
      usados,
      disponibles,
      cantidad,
      progreso,
      modoUso: 'servicio',
      vencida: false,
      fechaVencimientoLabel
    };
  }

  if (usados >= cantidad) {
    return {
      label: 'Usada / agotada',
      color: 'red',
      descripcion: `${usados} de ${cantidad} usados`,
      usados,
      disponibles,
      cantidad,
      progreso,
      modoUso: 'servicio',
      vencida: false,
      fechaVencimientoLabel
    };
  }

  const estadoVigencia = getEstadoVigencia({
    fechaVencimientoLabel,
    fallbackLabel: 'Uso parcial',
    fallbackColor: 'yellow',
    descripcionUso: `${disponibles} disponibles`
  });

  return {
    label: estadoVigencia.label,
    color: estadoVigencia.color,
    descripcion: estadoVigencia.descripcion,
    usados,
    disponibles,
    cantidad,
    progreso,
    modoUso: 'servicio',
    vencida: false,
    fechaVencimientoLabel
  };
};

const obtenerGiftcardsRelacionadas = (giftcard, giftcards) => {
  if (!giftcard) return [];

  const numeroId = (giftcard.numeroId || '').trim().toLowerCase();

  if (!numeroId) {
    return giftcards.filter((item) => item.id === giftcard.id);
  }

  return giftcards.filter((item) => (item.numeroId || '').trim().toLowerCase() === numeroId);
};

const obtenerMovimientosHistorial = (giftcardsRelacionadas) => (
  giftcardsRelacionadas
    .flatMap((giftcard) => (giftcard.historialUsos || []).map((uso, index) => ({
      ...uso,
      id: `${giftcard.id}-${index}`,
      giftcardId: giftcard.id,
      numeroId: giftcard.numeroId,
      cliente: giftcard.nombrePersona,
      servicio: giftcard.servicio,
      tipo: giftcard.tipo,
      modoUso: getModoUso(giftcard),
      tipoMovimiento: uso.tipo || 'servicio',
      usoNumero: index + 1,
      fechaNormalizada: normalizarFechaUso(uso.fecha)
    })))
    .sort((a, b) => {
      const fechaA = a.fechaNormalizada?.getTime() || 0;
      const fechaB = b.fechaNormalizada?.getTime() || 0;
      return fechaB - fechaA;
    })
);

const obtenerResumenHistorial = (giftcardsRelacionadas) => {
  const totalComprado = giftcardsRelacionadas.reduce((total, giftcard) => total + Number(giftcard.cantidad || 0), 0);
  const totalUsado = giftcardsRelacionadas.reduce((total, giftcard) => total + Number(giftcard.serviciosUsados || 0), 0);
  const montoInicial = giftcardsRelacionadas.reduce((total, giftcard) => total + Number(giftcard.montoInicial || 0), 0);
  const montoUsado = giftcardsRelacionadas.reduce((total, giftcard) => total + Number(giftcard.montoUsado || 0), 0);
  const saldoDisponible = giftcardsRelacionadas.reduce((total, giftcard) => total + Number(giftcard.saldoDisponible || 0), 0);

  return {
    totalComprado,
    totalUsado,
    totalDisponible: Math.max(totalComprado - totalUsado, 0),
    montoInicial,
    montoUsado,
    saldoDisponible: Math.max(saldoDisponible, 0)
  };
};

const GiftcardActions = ({ giftcard, onUsar, onEdit, onEliminar, onHistorial, compact = false }) => {
  const estado = obtenerEstadoUso(giftcard);
  const agotada = estado.disponibles <= 0;

  return (
    <Group gap={compact ? 6 : 'xs'} wrap="nowrap">
      <Button
        size="xs"
        variant={agotada ? 'light' : 'filled'}
        color={agotada ? 'gray' : 'teal'}
        onClick={() => onUsar(giftcard)}
        disabled={agotada}
        style={{ flexShrink: 0 }}
      >
        Usar
      </Button>
      <Tooltip label="Editar">
        <ActionIcon color="blue" variant="light" onClick={() => onEdit(giftcard)} aria-label="Editar giftcard">
          <IconEdit size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Ver historial de movimientos">
        <ActionIcon color="indigo" variant="light" onClick={() => onHistorial(giftcard)} aria-label="Ver historial">
          <IconHistory size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Eliminar">
        <ActionIcon color="red" variant="light" onClick={() => onEliminar(giftcard.id)} aria-label="Eliminar giftcard">
          <IconTrash size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
};

const EstadoUsoGiftcard = ({ giftcard }) => {
  const estado = obtenerEstadoUso(giftcard);
  const ultimoUso = obtenerUltimoUso(giftcard);

  return (
    <Stack gap={6}>
      <Group gap="xs" wrap="nowrap">
        <Badge color={estado.color} variant="light" radius="md">
          {estado.label}
        </Badge>
        <Text size="xs" c="dimmed">
          {estado.modoUso === 'monto'
            ? `$ ${formatearMonto(estado.usados)} usados`
            : `${estado.usados} usados`}
        </Text>
      </Group>
      <Progress
        value={estado.progreso}
        color={estado.color}
        radius="xl"
        size="sm"
        bg="gray.1"
      />
      <Text size="xs" c="dimmed">
        {estado.vencida || estado.fechaVencimientoLabel
          ? estado.descripcion
          : estado.modoUso === 'monto'
            ? `$ ${formatearMonto(estado.disponibles)} disponibles de $ ${formatearMonto(estado.cantidad)}`
            : `${estado.disponibles} disponibles de ${estado.cantidad || 0}`}
        {ultimoUso ? ` · último uso ${formatearFechaUso(ultimoUso)}` : ''}
      </Text>
    </Stack>
  );
};

// Vista responsive para resultados sin cargar toda la colección.
const TablaGiftcards = ({ data, onUsar, onEdit, onEliminar, onHistorial }) => {
  if (data.length === 0) {
    return (
      <Card withBorder radius="lg" p="xl" mt="md" bg="gray.0">
        <Center>
          <Stack gap="xs" align="center">
            <IconSearch size={30} color="#64748b" />
            <Text fw={700}>No se encontraron resultados</Text>
            <Text size="sm" c="dimmed" ta="center">
              Prueba con otro ID, documento o nombre.
            </Text>
          </Stack>
        </Center>
      </Card>
    );
  }

  return (
    <Stack gap="md" mt="md">
      {data.map((giftcard) => (
        <Card key={giftcard.id} withBorder radius="lg" p={{ base: 'md', md: 'lg' }} shadow="xs">
          <Stack gap="md">
            <Group justify="space-between" align="flex-start" gap="md">
              <Stack gap={4} style={{ minWidth: 0 }}>
                <Group gap="xs">
                  <Title order={4} style={{ fontSize: '1.05rem', lineHeight: 1.25 }}>
                    {giftcard.nombrePersona || 'Cliente no registrado'}
                  </Title>
                  <Badge color="gray" variant="outline" size="xs">
                    {getTipoLabel(giftcard.tipo)}
                  </Badge>
                </Group>
                {!esModoMonto(giftcard) ? (
                  <Text size="sm" c="dimmed">
                    {giftcard.servicio || 'Servicio no registrado'}
                  </Text>
                ) : null}
              </Stack>

              <GiftcardActions
                giftcard={giftcard}
                onUsar={onUsar}
                onEdit={onEdit}
                onEliminar={onEliminar}
                onHistorial={onHistorial}
                compact
              />
            </Group>

            <Divider />

            <Grid gutter={{ base: 'md', md: 'xl' }} align="center">
              <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
                <Text size="xs" c="dimmed" mb={3}>ID</Text>
                <Text size="sm" ff="monospace" fw={700} style={{ overflowWrap: 'anywhere' }}>
                  {giftcard.numeroId || 'Sin ID'}
                </Text>
              </Grid.Col>

              <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
                <Text size="xs" c="dimmed" mb={3}>{esModoMonto(giftcard) ? 'Saldo disponible' : 'Valor'}</Text>
                <Text fw={800}>
                  $ {formatearMonto(esModoMonto(giftcard) ? giftcard.saldoDisponible : giftcard.precioUnidad)}
                </Text>
                {esModoMonto(giftcard) ? (
                  <Text size="xs" c="dimmed">
                    Monto inicial: $ {formatearMonto(giftcard.montoInicial)}
                  </Text>
                ) : null}
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <EstadoUsoGiftcard giftcard={giftcard} />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
};

// Componente modal para crear giftcard
const GiftCardCreateModal = ({ opened, onClose, onSuccess, currentUser }) => {
  const [loading, setLoading] = useState(false);
  
  const form = useForm({
    initialValues: {
      tipo: 'giftcard',
      modoUso: 'monto',
      servicio: '',
      montoInicial: '',
      precioUnidad: '',
      cantidad: '',
      nombrePersona: '',
      clienteEmail: '',
      destinatarioEmail: '',
      regaloDe: '',
      regaloPara: '',
      numeroId: '',
      fechaCompra: new Date().toISOString().split('T')[0],
    },
    validate: {
      servicio: (value, values) => (
        values.modoUso !== 'monto' && !normalizarTextoFormulario(value)
          ? 'El servicio es requerido'
          : null
      ),
      montoInicial: (value, values) => (
        permiteUsoPorMonto(values.tipo) && values.modoUso === 'monto' && (!value || Number(value) <= 0)
          ? 'El monto inicial debe ser mayor a 0'
          : null
      ),
      precioUnidad: (value, values) => (
        values.modoUso !== 'monto' && (!value || Number(value) <= 0)
          ? 'El precio es requerido'
          : null
      ),
      cantidad: (value, values) => (
        values.modoUso !== 'monto' && (!value || Number(value) <= 0)
          ? 'La cantidad es requerida'
          : null
      ),
      nombrePersona: (value, values) => (
        !value && !(values.tipo === 'giftcard' && values.regaloPara)
          ? 'El nombre es requerido'
          : null
      ),
      clienteEmail: (value) => (
        value && !EMAIL_REGEX.test(normalizarEmail(value))
          ? 'Ingresa un correo de comprador válido'
          : null
      ),
      destinatarioEmail: (value, values) => {
        if (values.tipo !== 'giftcard') return null;
        const email = normalizarEmail(value);
        if (!email) return 'El correo del beneficiario es requerido';
        return EMAIL_REGEX.test(email) ? null : 'Ingresa un correo de beneficiario válido';
      },
      numeroId: (value) => (!normalizarTextoFormulario(value) ? 'El número de ID es requerido' : null),
      fechaCompra: (value) => (!value ? 'La fecha es requerida' : null),
    },
  });

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const esGiftcard = values.tipo === 'giftcard';
      const modoUso = permiteUsoPorMonto(values.tipo) && values.modoUso === 'monto' ? 'monto' : 'servicio';
      const montoInicial = Number(values.montoInicial || 0);
      const nombrePersona = esGiftcard && !values.nombrePersona
        ? values.regaloPara
        : values.nombrePersona;
      const datosNormalizados = {
        ...values,
        servicio: modoUso === 'monto' ? '' : normalizarTextoFormulario(values.servicio),
        nombrePersona: normalizarTextoFormulario(nombrePersona),
        regaloDe: normalizarTextoFormulario(values.regaloDe),
        regaloPara: normalizarTextoFormulario(values.regaloPara),
        numeroId: normalizarTextoFormulario(values.numeroId),
        clienteEmail: normalizarEmail(values.clienteEmail),
        destinatarioEmail: normalizarEmail(values.destinatarioEmail),
      };

      const payloadBase = {
        ...datosNormalizados,
        modoUso,
        precioUnidad: modoUso === 'monto' ? montoInicial : Number(values.precioUnidad || 0),
        cantidad: modoUso === 'monto' ? 1 : Number(values.cantidad || 0),
        fechaCreacion: serverTimestamp(),
        estado: 'activo',
        serviciosUsados: 0,
        creadoPor: currentUser?.email || '',
        historialUsos: [],
        origen: 'manual',
        clasificacionComercial: 'pagada'
      };

      const payloadMonto = modoUso === 'monto'
        ? {
          montoInicial,
          saldoDisponible: montoInicial,
          montoUsado: 0
        }
        : {};

      await addDoc(collection(db, 'giftcards'), {
        ...payloadBase,
        ...payloadMonto,
        ...obtenerCamposBusquedaGiftcard(datosNormalizados)
      });
      
      form.reset();
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error al crear giftcard:', error);
      alert('Error al crear giftcard');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Nueva Giftcard/Prepagado"
      size="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Select
            label="Tipo"
            placeholder="Selecciona el tipo"
            data={[
              { value: 'giftcard', label: 'Giftcard' },
              { value: 'prepagado', label: 'Servicio Prepagado' },
              { value: 'exento', label: 'Exento' }
            ]}
            value={form.values.tipo}
            onChange={(value) => {
              const tipo = value || 'giftcard';
              form.setFieldValue('tipo', tipo);
              form.setFieldValue('modoUso', permiteUsoPorMonto(tipo) ? form.values.modoUso || 'monto' : 'servicio');
            }}
          />

          {permiteUsoPorMonto(form.values.tipo) && (
            <Select
              label="Modo de uso"
              description="Por monto permite ir descontando pesos y ver saldo restante."
              data={[
                { value: 'monto', label: 'Por monto' },
                { value: 'servicio', label: 'Por servicios' }
              ]}
              {...form.getInputProps('modoUso')}
            />
          )}

          {form.values.modoUso !== 'monto' ? (
            <TextInput
              label="Servicio"
              placeholder="Nombre del servicio"
              {...form.getInputProps('servicio')}
            />
          ) : null}

          {permiteUsoPorMonto(form.values.tipo) && form.values.modoUso === 'monto' ? (
            <NumberInput
              label="Monto inicial"
              placeholder="Ingrese el monto disponible"
              {...form.getInputProps('montoInicial')}
              min={1}
              prefix="$"
              hideControls
              thousandSeparator="."
              decimalSeparator=","
              parser={(value) => {
                if (!value) return '';
                return value.replace(/[\$\s\.]/g, '');
              }}
              formatter={(value) => {
                if (!value) return '';
                const number = value.toString().replace(/\D/g, '');
                if (number === '') return '';
                return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
              }}
            />
          ) : (
            <>
              <NumberInput
                label="Precio por Unidad"
                placeholder="Ingrese el precio"
                {...form.getInputProps('precioUnidad')}
                min={0}
                prefix="$"
                hideControls
                thousandSeparator="."
                decimalSeparator=","
                parser={(value) => {
                  if (!value) return '';
                  return value.replace(/[\$\s\.]/g, '');
                }}
                formatter={(value) => {
                  if (!value) return '';
                  const number = value.toString().replace(/\D/g, '');
                  if (number === '') return '';
                  return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                }}
              />

              <NumberInput
                label="Cantidad de Servicio"
                placeholder="Ingrese la cantidad"
                {...form.getInputProps('cantidad')}
                min={1}
              />
            </>
          )}

          <TextInput
            label={form.values.tipo === 'giftcard' ? 'Nombre de la Persona' : 'Nombre de la Persona'}
            placeholder="Ingrese el nombre"
            {...form.getInputProps('nombrePersona')}
          />

          {form.values.tipo === 'giftcard' && (
            <>
              <TextInput
                label="De"
                placeholder="Nombre de quien regala"
                {...form.getInputProps('regaloDe')}
              />

              <TextInput
                label="Para"
                placeholder="Nombre de quien recibe el regalo"
                {...form.getInputProps('regaloPara')}
              />
            </>
          )}

          <TextInput
            inputMode="email"
            label={form.values.tipo === 'giftcard' ? 'Correo del comprador' : 'Correo del cliente'}
            description={form.values.tipo === 'giftcard'
              ? 'Opcional. Se guarda como comprador para respaldo interno.'
              : 'Opcional. Si se informa, se enviará el detalle automáticamente.'}
            placeholder="comprador@correo.com"
            {...form.getInputProps('clienteEmail')}
          />

          {form.values.tipo === 'giftcard' && (
            <TextInput
              inputMode="email"
              label="Correo del beneficiario"
              description="Recibirá la tarjeta regalo sin precio."
              placeholder="beneficiario@correo.com"
              {...form.getInputProps('destinatarioEmail')}
            />
          )}

          <TextInput
            label="Número de ID"
            placeholder="Ingrese el número de ID"
            {...form.getInputProps('numeroId')}
          />

          <TextInput
            type="date"
            label="Fecha de Compra"
            {...form.getInputProps('fechaCompra')}
          />

          <Button type="submit" loading={loading}>
            Crear Giftcard/Prepagado
          </Button>
        </Stack>
      </form>
    </Modal>
  );
};

// Componente modal para editar giftcard
const GiftCardEditModal = ({ opened, onClose, giftcard, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const editForm = useForm({
    initialValues: {
      montoInicial: '',
      cantidad: '',
      precioUnidad: '',
    },
    validate: {
      montoInicial: (value) => (
        giftcard && esModoMonto(giftcard) && (!value || Number(value) <= 0)
          ? 'El monto inicial debe ser mayor a 0'
          : null
      ),
      cantidad: (value) => (
        giftcard && !esModoMonto(giftcard) && (!value || Number(value) <= 0)
          ? 'La cantidad es requerida'
          : null
      ),
      precioUnidad: (value) => (
        giftcard && !esModoMonto(giftcard) && (!value || Number(value) <= 0)
          ? 'El precio es requerido'
          : null
      ),
    },
  });

  // Actualizar valores del formulario cuando cambia la giftcard seleccionada
  useEffect(() => {
    if (giftcard) {
      editForm.setValues({
        montoInicial: giftcard.montoInicial || giftcard.precioUnidad || '',
        cantidad: giftcard.cantidad,
        precioUnidad: giftcard.precioUnidad,
      });
    }
  }, [giftcard]);

  const handleEditSubmit = async (values) => {
    try {
      setLoading(true);
      if (esModoMonto(giftcard)) {
        const montoInicial = Number(values.montoInicial || 0);
        const montoUsado = Number(giftcard.montoUsado || 0);

        if (montoUsado > 0) {
          throw new Error('No se puede editar el monto inicial cuando la giftcard ya tiene consumos');
        }

        await updateDoc(doc(db, 'giftcards', giftcard.id), {
          montoInicial,
          saldoDisponible: montoInicial,
          precioUnidad: montoInicial,
        });
      } else {
        await updateDoc(doc(db, 'giftcards', giftcard.id), {
          cantidad: Number(values.cantidad),
          precioUnidad: Number(values.precioUnidad),
        });
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error al actualizar giftcard:', error);
      alert(error.message || 'Error al actualizar giftcard');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Editar ${getTipoLabel(giftcard?.tipo)}`}
      size="md"
    >
      <form onSubmit={editForm.onSubmit(handleEditSubmit)}>
        <Stack>
          {giftcard && esModoMonto(giftcard) ? (
            <NumberInput
              label="Monto inicial"
              description={`Ya usado: $ ${formatearMonto(giftcard.montoUsado || 0)}`}
              placeholder="Ingrese el monto inicial"
              {...editForm.getInputProps('montoInicial')}
              min={Number(giftcard.montoUsado || 0)}
              prefix="$"
              hideControls
              thousandSeparator="."
              decimalSeparator=","
              parser={(value) => {
                if (!value) return '';
                return value.replace(/[\$\s\.]/g, '');
              }}
              formatter={(value) => {
                if (!value) return '';
                const number = value.toString().replace(/\D/g, '');
                if (number === '') return '';
                return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
              }}
            />
          ) : (
            <>
              <NumberInput
                label="Cantidad de Servicio"
                placeholder="Ingrese la cantidad"
                {...editForm.getInputProps('cantidad')}
                min={1}
              />

              <NumberInput
                label="Precio por Unidad"
                placeholder="Ingrese el precio"
                {...editForm.getInputProps('precioUnidad')}
                min={0}
                prefix="$"
                hideControls
                thousandSeparator="."
                decimalSeparator=","
                parser={(value) => {
                  if (!value) return '';
                  return value.replace(/[\$\s\.]/g, '');
                }}
                formatter={(value) => {
                  if (!value) return '';
                  const number = value.toString().replace(/\D/g, '');
                  if (number === '') return '';
                  return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                }}
              />
            </>
          )}

          <Button type="submit" loading={loading}>
            Guardar Cambios
          </Button>
        </Stack>
      </form>
    </Modal>
  );
};

const GiftCardHistoryModal = ({ opened, onClose, giftcard, giftcards }) => {
  const giftcardsRelacionadas = obtenerGiftcardsRelacionadas(giftcard, giftcards);
  const movimientos = obtenerMovimientosHistorial(giftcardsRelacionadas);
  const resumen = obtenerResumenHistorial(giftcardsRelacionadas);
  const clientes = [...new Set(giftcardsRelacionadas.map((item) => item.nombrePersona).filter(Boolean))];
  const historialPorMonto = giftcard ? esModoMonto(giftcard) : false;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Historial de movimientos"
      size="lg"
    >
      {!giftcard ? null : (
        <Stack>
          <Group position="apart" align="flex-start">
            <Stack spacing={2}>
              <Text size="sm" color="dimmed">ID buscado</Text>
              <Text fw={700}>{giftcard.numeroId || giftcard.id}</Text>
              <Text size="sm" color="dimmed">
                {clientes.length > 0 ? clientes.join(', ') : 'Cliente no registrado'}
              </Text>
            </Stack>
            <Badge color={(historialPorMonto ? resumen.saldoDisponible : resumen.totalDisponible) > 0 ? 'green' : 'red'} variant="light">
              {(historialPorMonto ? resumen.saldoDisponible : resumen.totalDisponible) > 0 ? 'Disponible' : 'Agotado'}
            </Badge>
          </Group>

          <Group grow>
            <Card withBorder p="sm">
              <Text size="xs" color="dimmed">{historialPorMonto ? 'Monto inicial' : 'Comprados'}</Text>
              <Text fw={700}>
                {historialPorMonto ? `$ ${formatearMonto(resumen.montoInicial)}` : resumen.totalComprado}
              </Text>
            </Card>
            <Card withBorder p="sm">
              <Text size="xs" color="dimmed">Usados</Text>
              <Text fw={700}>
                {historialPorMonto ? `$ ${formatearMonto(resumen.montoUsado)}` : resumen.totalUsado}
              </Text>
            </Card>
            <Card withBorder p="sm">
              <Text size="xs" color="dimmed">{historialPorMonto ? 'Saldo' : 'Disponibles'}</Text>
              <Text fw={700}>
                {historialPorMonto ? `$ ${formatearMonto(resumen.saldoDisponible)}` : resumen.totalDisponible}
              </Text>
            </Card>
          </Group>

          <Divider label="Giftcards relacionadas" labelPosition="center" />

          <Stack spacing="xs">
            {giftcardsRelacionadas.map((item) => {
              const estado = obtenerEstadoUso(item);

              return (
                <Card key={item.id} withBorder p="sm">
                  <Group position="apart" align="flex-start">
                    <Stack spacing={2}>
                      {!esModoMonto(item) ? (
                        <Text fw={600}>{item.servicio || 'Servicio no registrado'}</Text>
                      ) : null}
                      <Text size="sm" color="dimmed">
                        {item.nombrePersona || 'Cliente no registrado'} · {esModoMonto(item)
                          ? `$ ${formatearMonto(item.saldoDisponible)} de $ ${formatearMonto(item.montoInicial)}`
                          : `$ ${formatearMonto(item.precioUnidad)}`}
                      </Text>
                    </Stack>
                    <Badge color={estado.disponibles > 0 ? 'green' : 'red'} variant="light">
                      {esModoMonto(item)
                        ? `$ ${formatearMonto(estado.disponibles)}`
                        : `${estado.disponibles} de ${item.cantidad || 0}`}
                    </Badge>
                  </Group>
                </Card>
              );
            })}
          </Stack>

          <Divider label="Usos registrados" labelPosition="center" />

          {movimientos.length === 0 ? (
            <Card withBorder p="md">
              <Text color="dimmed" align="center">Sin usos registrados</Text>
            </Card>
          ) : (
            <ScrollArea h={320}>
              <Stack spacing="xs">
                {movimientos.map((movimiento) => (
                  <Card key={movimiento.id} withBorder p="sm">
                    <Group position="apart" align="flex-start">
                      <Stack spacing={2}>
                        <Text fw={600}>{formatearFechaUso(movimiento.fecha)}</Text>
                        <Text size="sm" color="dimmed">
                          {movimiento.modoUso === 'monto'
                            ? movimiento.cliente || 'Cliente no registrado'
                            : `${movimiento.servicio || 'Servicio no registrado'} · ${movimiento.cliente || 'Cliente no registrado'}`}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {movimiento.tipoMovimiento === 'monto'
                            ? `Descuento: $ ${formatearMonto(movimiento.monto)} · Saldo: $ ${formatearMonto(movimiento.saldoAnterior)} -> $ ${formatearMonto(movimiento.saldoPosterior)}`
                            : `Origen: ${movimiento.numeroId || movimiento.giftcardId} · Uso #${movimiento.usoNumero}`}
                        </Text>
                        {movimiento.observacion ? (
                          <Text size="xs" color="dimmed">{movimiento.observacion}</Text>
                        ) : null}
                      </Stack>
                      <Badge variant="light" color="blue">
                        {movimiento.usuario || 'Usuario no registrado'}
                      </Badge>
                    </Group>
                  </Card>
                ))}
              </Stack>
            </ScrollArea>
          )}
        </Stack>
      )}
    </Modal>
  );
};

const getDetalleActividad = (actividad) => {
  if (actividad.eventType === 'created') {
    if (actividad.modoUso === 'monto') {
      return `Creada por $ ${formatearMonto(actividad.monto)}`;
    }
    return `${actividad.cantidad || 1} servicio(s) · $ ${formatearMonto(actividad.monto)} c/u`;
  }

  if (actividad.modoUso === 'monto') {
    return `Canje $ ${formatearMonto(actividad.monto)} · saldo $ ${formatearMonto(actividad.saldoPosterior)}`;
  }
  return `Canje de servicio${actividad.usoNumero ? ` · uso #${actividad.usoNumero}` : ''}`;
};

const GiftcardRecentActivity = () => {
  const [actividades, setActividades] = useState([]);
  const [filtro, setFiltro] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const activityQuery = query(
      collection(db, 'giftcardActivity'),
      orderBy('occurredAt', 'desc'),
      limit(ACTIVITY_LIMIT)
    );

    return onSnapshot(activityQuery, (snapshot) => {
      setActividades(snapshot.docs.map((activityDoc) => ({
        id: activityDoc.id,
        ...activityDoc.data(),
      })));
      setError('');
      setLoading(false);
    }, (snapshotError) => {
      console.error('Error al cargar historial reciente de giftcards:', snapshotError);
      setError('No se pudo cargar el historial reciente.');
      setLoading(false);
    });
  }, []);

  const actividadesFiltradas = actividades.filter((actividad) => (
    filtro === 'all' || actividad.eventType === filtro
  ));

  return (
    <Card withBorder radius="lg" p={{ base: 'md', md: 'lg' }} mb="lg" bg="white">
      <Group justify="space-between" align="flex-start" gap="md" mb="md">
        <Stack gap={3}>
          <Group gap="xs">
            <IconHistory size={20} color="#4f46e5" />
            <Text fw={800}>Historial reciente</Text>
          </Group>
          <Text size="sm" c="dimmed">Últimas 15 creaciones y canjes registrados.</Text>
        </Stack>
        <SegmentedControl
          size="xs"
          value={filtro}
          onChange={setFiltro}
          data={[
            { value: 'all', label: 'Todos' },
            { value: 'created', label: 'Creadas' },
            { value: 'redeemed', label: 'Canjeadas' },
          ]}
        />
      </Group>

      {loading ? (
        <Center py="lg"><Loader size="sm" /></Center>
      ) : error ? (
        <Alert color="red" icon={<IconAlertCircle size={18} />} radius="md">{error}</Alert>
      ) : actividadesFiltradas.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" py="lg">
          No hay movimientos recientes para este filtro.
        </Text>
      ) : (
        <Stack gap="xs">
          {actividadesFiltradas.map((actividad) => {
            const esCreacion = actividad.eventType === 'created';
            return (
              <Box
                key={actividad.id}
                p="sm"
                style={{
                  border: '1px solid var(--mantine-color-gray-2)',
                  borderRadius: 'var(--mantine-radius-md)',
                }}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                  <Group align="flex-start" wrap="nowrap" gap="sm" style={{ minWidth: 0 }}>
                    <Box
                      p={7}
                      bg={esCreacion ? 'indigo.0' : 'teal.0'}
                      style={{ borderRadius: 10, flexShrink: 0, lineHeight: 0 }}
                    >
                      {esCreacion
                        ? <IconPlus size={16} color="#4f46e5" />
                        : <IconCircleCheck size={16} color="#0f766e" />}
                    </Box>
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Group gap="xs">
                        <Text fw={700} size="sm">{actividad.cliente || 'Cliente no registrado'}</Text>
                        <Badge size="xs" color={esCreacion ? 'indigo' : 'teal'} variant="light">
                          {esCreacion ? 'Creada' : 'Canjeada'}
                        </Badge>
                      </Group>
                      <Text size="sm" c="dimmed">
                        {actividad.numeroId || actividad.giftcardId} · {getDetalleActividad(actividad)}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {actividad.modoUso === 'monto'
                          ? actividad.usuario || 'Sistema'
                          : `${actividad.servicio || 'Servicio no registrado'} · ${actividad.usuario || 'Sistema'}`}
                      </Text>
                    </Stack>
                  </Group>
                  <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                    {formatearFechaActividad(actividad.occurredAt)}
                  </Text>
                </Group>
              </Box>
            );
          })}
        </Stack>
      )}
    </Card>
  );
};

const GiftCards = () => {
  const { currentUser } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedGiftcard, setSelectedGiftcard] = useState(null);
  const [historyGiftcard, setHistoryGiftcard] = useState(null);
  const [historyGiftcards, setHistoryGiftcards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [seccionActiva, setSeccionActiva] = useState('gestion');
  const [searchId, setSearchId] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [giftcards, setGiftcards] = useState([]);
  const searchRequestRef = useRef(0);
  const usoMontoForm = useForm({
    initialValues: {
      monto: '',
      observacion: '',
    },
    validate: {
      monto: (value) => {
        const monto = Number(value || 0);
        const saldoDisponible = Number(selectedGiftcard?.saldoDisponible || 0);

        if (monto <= 0) return 'El monto debe ser mayor a 0';
        if (monto > saldoDisponible) return 'El monto no puede superar el saldo disponible';
        return null;
      },
    },
  });

  const ejecutarBusqueda = useCallback(async (terminoOriginal) => {
    const termino = normalizarTerminoBusqueda(terminoOriginal);
    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;

    if (!termino) {
      setGiftcards([]);
      setHasSearched(false);
      setSearchError('');
      setLoading(false);
      return [];
    }

    try {
      setLoading(true);
      setSearchError('');

      const giftcardsRef = collection(db, 'giftcards');
      const variantes = crearVariantesBusqueda(termino);
      const terminoNormalizado = normalizarValorBusqueda(termino);
      const numeroBusqueda = getNumeroBusqueda(termino);
      const consultas = [];

      consultas.push(getDocs(query(
        giftcardsRef,
        orderBy('numeroIdBusqueda'),
        startAt(terminoNormalizado),
        endAt(`${terminoNormalizado}\uf8ff`),
        limit(RESULT_LIMIT)
      )));
      consultas.push(getDocs(query(
        giftcardsRef,
        orderBy('nombrePersonaBusqueda'),
        startAt(terminoNormalizado),
        endAt(`${terminoNormalizado}\uf8ff`),
        limit(RESULT_LIMIT)
      )));

      variantes.forEach((variante) => {
        consultas.push(getDocs(query(
          giftcardsRef,
          where('numeroId', '==', variante),
          limit(RESULT_LIMIT)
        )));
        consultas.push(getDocs(query(
          giftcardsRef,
          orderBy('numeroId'),
          startAt(variante),
          endAt(`${variante}\uf8ff`),
          limit(RESULT_LIMIT)
        )));
        consultas.push(getDocs(query(
          giftcardsRef,
          orderBy('nombrePersona'),
          startAt(variante),
          endAt(`${variante}\uf8ff`),
          limit(RESULT_LIMIT)
        )));
      });

      if (numeroBusqueda !== null) {
        consultas.push(getDocs(query(
          giftcardsRef,
          where('numeroId', '==', numeroBusqueda),
          limit(RESULT_LIMIT)
        )));
      }

      if (!termino.includes('/')) {
        consultas.push(
          getDoc(doc(db, 'giftcards', termino)).then((snapshot) => ({
            docs: snapshot.exists() ? [snapshot] : []
          }))
        );
      }

      const snapshots = await Promise.all(consultas);
      const resultados = mezclarResultadosGiftcards(
        snapshots.map((snapshot) => snapshot.docs.map(mapDocGiftcard))
      );

      if (searchRequestRef.current !== requestId) {
        return [];
      }

      setGiftcards(resultados);
      setHasSearched(true);
      return resultados;
    } catch (error) {
      if (searchRequestRef.current !== requestId) {
        return [];
      }

      console.error('Error al buscar giftcards:', error);
      setGiftcards([]);
      setHasSearched(true);
      setSearchError('No se pudo completar la búsqueda. Revisa conexión o índices de Firestore.');
      return [];
    } finally {
      if (searchRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const termino = normalizarTerminoBusqueda(searchId);

    if (!termino) {
      searchRequestRef.current += 1;
      setGiftcards([]);
      setHasSearched(false);
      setSearchError('');
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const timeoutId = window.setTimeout(() => {
      ejecutarBusqueda(termino);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchId, ejecutarBusqueda]);

  const refrescarBusquedaActual = useCallback(() => {
    if (normalizarTerminoBusqueda(searchId)) {
      return ejecutarBusqueda(searchId);
    }
    return Promise.resolve([]);
  }, [ejecutarBusqueda, searchId]);

  const handleEdit = (giftcard) => {
    setSelectedGiftcard(giftcard);
    setShowEditForm(true);
  };

  const handleHistorial = async (giftcard) => {
    setHistoryGiftcard(giftcard);
    setHistoryGiftcards([giftcard]);
    setShowHistoryModal(true);

    const numeroId = normalizarTextoFormulario(giftcard.numeroId);
    if (!numeroId) return;

    try {
      const giftcardsRef = collection(db, 'giftcards');
      const numeroBusqueda = getNumeroBusqueda(numeroId);
      const consultas = [
        getDocs(query(
          giftcardsRef,
          where('numeroId', '==', numeroId),
          limit(RESULT_LIMIT)
        ))
      ];

      if (numeroBusqueda !== null) {
        consultas.push(getDocs(query(
          giftcardsRef,
          where('numeroId', '==', numeroBusqueda),
          limit(RESULT_LIMIT)
        )));
      }

      const snapshots = await Promise.all(consultas);
      const relacionadas = mezclarResultadosGiftcards(
        snapshots.map((snapshot) => snapshot.docs.map(mapDocGiftcard))
      );
      setHistoryGiftcards(relacionadas.length > 0 ? relacionadas : [giftcard]);
    } catch (error) {
      console.error('Error al cargar historial relacionado:', error);
      setHistoryGiftcards([giftcard]);
    }
  };

  const handleUsarServicio = async (giftcard) => {
    const estado = obtenerEstadoUso(giftcard);

    if (estado.vencida) {
      alert(estado.descripcion || 'Esta giftcard está vencida');
      return;
    }

    if (estado.disponibles <= 0) {
      alert(esModoMonto(giftcard) ? 'No hay saldo disponible' : 'No hay servicios disponibles');
      return;
    }

    setSelectedGiftcard(giftcard);
    usoMontoForm.reset();
    setShowConfirmModal(true);
  };

  const confirmarUsoServicio = async (values = {}) => {
    try {
      if (!selectedGiftcard) return;

      if (esModoMonto(selectedGiftcard)) {
        const montoDescontar = Number(values.monto || 0);

        if (montoDescontar <= 0) {
          alert('El monto debe ser mayor a 0');
          return;
        }

        await runTransaction(db, async (transaction) => {
          const giftcardRef = doc(db, 'giftcards', selectedGiftcard.id);
          const giftcardSnap = await transaction.get(giftcardRef);

          if (!giftcardSnap.exists()) {
            throw new Error('La giftcard ya no existe');
          }

          const giftcardActual = giftcardSnap.data();
          const saldoAnterior = Number(giftcardActual.saldoDisponible || 0);
          const montoUsadoActual = Number(giftcardActual.montoUsado || 0);

          if (montoDescontar > saldoAnterior) {
            throw new Error('El monto supera el saldo disponible');
          }

          const saldoPosterior = saldoAnterior - montoDescontar;
          const nuevoUso = {
            tipo: 'monto',
            monto: montoDescontar,
            saldoAnterior,
            saldoPosterior,
            fecha: new Date().toISOString(),
            usuario: currentUser?.email || '',
            observacion: normalizarTextoFormulario(values.observacion || '')
          };

          transaction.update(giftcardRef, {
            saldoDisponible: saldoPosterior,
            montoUsado: montoUsadoActual + montoDescontar,
            historialUsos: [...(giftcardActual.historialUsos || []), nuevoUso]
          });
        });

        usoMontoForm.reset();
        setShowConfirmModal(false);
        setSelectedGiftcard(null);
        await refrescarBusquedaActual();
        return;
      }

      const nuevoUso = {
        tipo: 'servicio',
        fecha: new Date().toISOString(),
        usuario: currentUser?.email
      };

      await updateDoc(doc(db, 'giftcards', selectedGiftcard.id), {
        serviciosUsados: selectedGiftcard.serviciosUsados + 1,
        historialUsos: [...(selectedGiftcard.historialUsos || []), nuevoUso]
      });
      setShowConfirmModal(false);
      setSelectedGiftcard(null);
      await refrescarBusquedaActual();
    } catch (error) {
      console.error('Error al actualizar servicio:', error);
      alert(error.message || 'Error al actualizar servicio');
    }
  };

  const handleEliminar = async (giftcardId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta giftcard?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'giftcards', giftcardId));
      await refrescarBusquedaActual();
    } catch (error) {
      console.error('Error al eliminar giftcard:', error);
      alert('Error al eliminar giftcard');
    }
  };

  const giftcardsFiltrados = giftcards.filter(giftcard => {
    const cumpleFiltroTipo = filtroTipo === 'todos' || giftcard.tipo === filtroTipo;
    return cumpleFiltroTipo;
  });

  const giftcardsDisponibles = giftcardsFiltrados.filter(giftcard =>
    obtenerEstadoUso(giftcard).disponibles > 0
  );

  const giftcardsAgotados = giftcardsFiltrados.filter(giftcard =>
    obtenerEstadoUso(giftcard).disponibles <= 0
  );

  return (
    <Container size="xl" py="md">
      <Paper shadow="sm" p={{ base: 'md', md: 'xl' }} radius="xl" withBorder className="surface-panel">
        <Group justify="space-between" align="flex-start" mb="xl" gap="md">
          <Stack gap={6} style={{ flex: 1, minWidth: 260 }}>
            <Badge color="indigo" variant="light" w="fit-content">
              Módulo operativo
            </Badge>
            <Title order={2} style={{ letterSpacing: '-0.03em' }}>
              Giftcards y prepagados
            </Title>
            <Text c="dimmed" maw={680}>
              Busca por ID, documento o nombre para revisar saldo, estado de uso y movimientos sin cargar todo el registro.
            </Text>
          </Stack>
          <Button
            leftIcon={<IconPlus size={20} />}
            onClick={() => setShowForm(true)}
            radius="md"
            style={{ flexShrink: 0 }}
          >
            Nueva Giftcard/Prepagado
          </Button>
        </Group>

        <SegmentedControl
          value={seccionActiva}
          onChange={setSeccionActiva}
          fullWidth
          mb="lg"
          data={[
            { value: 'gestion', label: 'Gestión' },
            { value: 'ventas', label: 'Ventas' },
          ]}
        />

        {seccionActiva === 'ventas' ? <GiftcardSalesReport /> : <>

        <Card withBorder radius="lg" p={{ base: 'md', md: 'lg' }} mb="lg" bg="white">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" verticalSpacing="md">
            <TextInput
              label="Buscar giftcard"
              description="ID, documento o nombre de cliente"
              placeholder="Ej: 12345678, GC-001 o Ana"
              value={searchId}
              onChange={(event) => setSearchId(event.currentTarget.value)}
              leftSection={<IconSearch size={18} />}
              rightSection={
                searchId ? (
                  <ActionIcon onClick={() => setSearchId('')} variant="subtle" color="gray" aria-label="Limpiar búsqueda">
                    <IconX size={16} />
                  </ActionIcon>
                ) : null
              }
            />
            <Select
              label="Tipo"
              description="Filtro aplicado a los resultados encontrados"
              value={filtroTipo}
              onChange={setFiltroTipo}
              data={[
                { value: 'todos', label: 'Todos los tipos' },
                { value: 'giftcard', label: 'Giftcards' },
                { value: 'prepagado', label: 'Servicios Prepagados' },
                { value: 'exento', label: 'Exento' }
              ]}
            />
          </SimpleGrid>
        </Card>

        {searchError ? (
          <Alert color="red" icon={<IconAlertCircle size={18} />} mb="lg" radius="md">
            {searchError}
          </Alert>
        ) : null}

        {!hasSearched && !loading ? (
          <Card withBorder radius="xl" p={{ base: 'xl', md: 42 }} bg="gray.0">
            <Center>
              <Stack align="center" gap="sm" maw={460}>
                <Box
                  p="md"
                  bg="white"
                  style={{
                    borderRadius: 18,
                    border: '1px solid rgba(226, 232, 240, 0.95)',
                    boxShadow: '0 16px 34px rgba(15, 23, 42, 0.06)'
                  }}
                >
                  <IconGiftCard size={34} color="#334155" />
                </Box>
                <Title order={3} ta="center" style={{ letterSpacing: '-0.02em' }}>
                  Busca por ID, documento o nombre
                </Title>
                <Text c="dimmed" ta="center">
                  La lista completa ya no se carga al entrar. Escribe un dato de la giftcard para ver solo los resultados relevantes.
                </Text>
              </Stack>
            </Center>
          </Card>
        ) : null}

        {loading ? (
          <Card withBorder radius="lg" p="xl" bg="white">
            <Center>
              <Group gap="sm">
                <Loader size="sm" />
                <Text fw={600}>Buscando giftcards...</Text>
              </Group>
            </Center>
          </Card>
        ) : null}

        {hasSearched && !loading ? (
          <>
            <Group justify="space-between" align="center" mb="sm">
              <Group gap="xs">
                <IconCircleCheck size={18} color="#0f766e" />
                <Text fw={700}>{giftcardsFiltrados.length} resultados</Text>
                <Text size="sm" c="dimmed">
                  para “{normalizarTerminoBusqueda(searchId)}”
                </Text>
              </Group>
            </Group>

            <Tabs defaultValue="disponibles" radius="md">
              <Tabs.List>
                <Tabs.Tab
                  value="disponibles"
                  rightSection={
                    <Badge size="sm" variant="filled" color="green">
                      {giftcardsDisponibles.length}
                    </Badge>
                  }
                >
                  Disponibles
                </Tabs.Tab>
                <Tabs.Tab
                  value="agotados"
                  rightSection={
                    <Badge size="sm" variant="filled" color="red">
                      {giftcardsAgotados.length}
                    </Badge>
                  }
                >
                  Usadas / agotadas
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="disponibles" pt="xs">
                <TablaGiftcards
                  data={giftcardsDisponibles}
                  onUsar={handleUsarServicio}
                  onEdit={handleEdit}
                  onEliminar={handleEliminar}
                  onHistorial={handleHistorial}
                />
              </Tabs.Panel>

              <Tabs.Panel value="agotados" pt="xs">
                <TablaGiftcards
                  data={giftcardsAgotados}
                  onUsar={handleUsarServicio}
                  onEdit={handleEdit}
                  onEliminar={handleEliminar}
                  onHistorial={handleHistorial}
                />
              </Tabs.Panel>
            </Tabs>
          </>
        ) : null}

        <GiftcardRecentActivity />
        </>}

        <GiftCardCreateModal 
          opened={showForm}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            refrescarBusquedaActual();
          }}
          currentUser={currentUser}
        />

        <GiftCardEditModal
          opened={showEditForm}
          onClose={() => {
            setShowEditForm(false);
            setSelectedGiftcard(null);
          }}
          giftcard={selectedGiftcard}
          onSuccess={() => {
             setSelectedGiftcard(null);
             refrescarBusquedaActual();
          }}
        />

        <GiftCardHistoryModal
          opened={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setHistoryGiftcard(null);
            setHistoryGiftcards([]);
          }}
          giftcard={historyGiftcard}
          giftcards={historyGiftcards.length > 0 ? historyGiftcards : giftcards}
        />

        <Modal
          opened={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            setSelectedGiftcard(null);
            usoMontoForm.reset();
          }}
          title={selectedGiftcard && esModoMonto(selectedGiftcard) ? 'Descontar saldo' : 'Confirmar uso de servicio'}
          size="md"
        >
          {selectedGiftcard && esModoMonto(selectedGiftcard) ? (
            <form onSubmit={usoMontoForm.onSubmit(confirmarUsoServicio)}>
              <Stack>
                <Text size="sm" c="dimmed">
                  Saldo disponible: $ {formatearMonto(selectedGiftcard.saldoDisponible)}
                </Text>
                <NumberInput
                  label="Monto a descontar"
                  placeholder="Ingrese el monto usado"
                  {...usoMontoForm.getInputProps('monto')}
                  min={1}
                  max={Number(selectedGiftcard.saldoDisponible || 0)}
                  prefix="$"
                  hideControls
                  thousandSeparator="."
                  decimalSeparator=","
                  parser={(value) => {
                    if (!value) return '';
                    return value.replace(/[\$\s\.]/g, '');
                  }}
                  formatter={(value) => {
                    if (!value) return '';
                    const number = value.toString().replace(/\D/g, '');
                    if (number === '') return '';
                    return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                  }}
                />
                <Textarea
                  label="Observación"
                  placeholder="Ej: corte, barba, producto o nota interna"
                  minRows={3}
                  {...usoMontoForm.getInputProps('observacion')}
                />
                <Group position="right">
                  <Button variant="default" onClick={() => {
                    setShowConfirmModal(false);
                    setSelectedGiftcard(null);
                    usoMontoForm.reset();
                  }}>
                    Cancelar
                  </Button>
                  <Button color="green" type="submit">
                    Descontar saldo
                  </Button>
                </Group>
              </Stack>
            </form>
          ) : (
            <Stack>
              <Text>
                ¿Estás seguro de que deseas usar un servicio de esta giftcard?
              </Text>
              {selectedGiftcard && (
                <Text size="sm" color="dimmed">
                  Servicios restantes: {selectedGiftcard.cantidad - selectedGiftcard.serviciosUsados} de {selectedGiftcard.cantidad}
                </Text>
              )}
              <Group position="right">
                <Button variant="default" onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedGiftcard(null);
                }}>
                  Cancelar
                </Button>
                <Button color="green" onClick={() => confirmarUsoServicio()}>
                  Confirmar Uso
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </Paper>
    </Container>
  );
};

export default GiftCards;
